/// <reference types="vite/client" />
import { useCallback, useEffect, useState } from "react";
import { ErrorBanner } from "../components/ErrorBanner";
import { useEngagement } from "../hooks/useEngagement";

// ─── Wire-format ──────────────────────────────────────────────────────
// Mirror of `CeoDecisionRow` from src/api/ceo-decisions.ts as the JSON
// shape over the wire. Kept local to avoid coupling the dashboard bundle
// to the worker-side drizzle types.

interface CeoDecision {
  id: string;
  /** Unix seconds */
  createdAt: number;
  engagementId: string | null;
  escalation: {
    id: string;
    from: string;
    severity: string;
    reason: string;
  };
  decision: {
    verdict: "allow" | "deny" | "modify";
    reason: string;
    conditions?: string[];
    alertFounders?: boolean;
  };
}

// ─── Token maps ───────────────────────────────────────────────────────

const VERDICT_TOKENS: Record<CeoDecision["decision"]["verdict"], { color: string; border: string; bg: string; label: string }> = {
  allow:  { color: "var(--color-status-active)", border: "rgba(132, 204, 22, 0.4)", bg: "rgba(132, 204, 22, 0.08)", label: "ALLOW"  },
  modify: { color: "var(--color-status-paused)", border: "rgba(245, 158, 11, 0.45)", bg: "rgba(245, 158, 11, 0.10)", label: "MODIFY" },
  deny:   { color: "var(--color-status-error)",  border: "rgba(239, 68, 68, 0.4)",  bg: "rgba(239, 68, 68, 0.08)", label: "DENY"   },
};

function severityTokens(sev: string): { color: string; border: string; bg: string } {
  const s = sev.toLowerCase();
  if (s === "critical") return { color: "var(--color-status-error)",  border: "rgba(239, 68, 68, 0.5)",  bg: "rgba(239, 68, 68, 0.10)" };
  if (s === "high")     return { color: "var(--color-status-paused)", border: "rgba(245, 158, 11, 0.45)", bg: "rgba(245, 158, 11, 0.10)" };
  if (s === "medium")   return { color: "var(--color-accent)",         border: "rgba(245, 158, 11, 0.35)", bg: "rgba(245, 158, 11, 0.06)" };
  return { color: "var(--color-text-dim)", border: "rgba(120, 113, 108, 0.35)", bg: "rgba(120, 113, 108, 0.06)" };
}

function formatTimestamp(unixSeconds: number): string {
  if (!unixSeconds) return "—";
  const d = new Date(unixSeconds * 1000);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

// ─── Page ─────────────────────────────────────────────────────────────

export function Decisions() {
  // Y5 (#165) — engagement comes from the global picker context.
  const { engagementId: engagement } = useEngagement();
  const [decisions, setDecisions] = useState<CeoDecision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (engagement) params.set("engagement", engagement);
      fetch(`/api/ceo/decisions?${params.toString()}`, { signal })
        .then(async (res) => {
          if (!res.ok) {
            const body = await res.text().catch(() => "");
            throw new Error(`HTTP ${res.status}${body ? ` — ${body.slice(0, 200)}` : ""}`);
          }
          return res.json() as Promise<CeoDecision[]>;
        })
        .then((rows) => {
          setDecisions(rows);
          setLoading(false);
        })
        .catch((err: Error) => {
          if (err.name === "AbortError") return;
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn("[Decisions] /api/ceo/decisions fetch failed.", err);
          }
          setError(err.message);
          setLoading(false);
        });
    },
    [engagement],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  if (error && decisions.length === 0) {
    return <ErrorBanner message={error} onRetry={load} context="CEO decisions" />;
  }

  return (
    <div className="space-y-8">
      {/* Page header */}
      <header className="reveal" style={{ animationDelay: "0ms" }}>
        <p className="eyebrow">Console · 05</p>
        <h2 className="font-display italic text-3xl sm:text-4xl lg:text-[56px] leading-[0.95] mt-2 tracking-tight">
          <span className="text-[var(--color-text)]">CEO </span>
          <span className="text-accent-gradient">Decisions</span>
        </h2>
        <p className="mt-3 text-sm text-[var(--color-text-muted)] max-w-2xl">
          Recent CEO deliberations on agent escalations. Each verdict is appended to the audit ledger.
        </p>
      </header>

      {loading && decisions.length === 0 && (
        <div className="flex items-center gap-3 text-[var(--color-text-dim)]">
          <span className="live-dot text-[var(--color-accent)]" />
          <span className="font-mono text-xs tracking-[0.18em] uppercase blink">Loading CEO decisions…</span>
        </div>
      )}

      {!loading && decisions.length === 0 && (
        <div className="panel p-8 sm:p-12 text-center">
          <p className="eyebrow">No deliberations</p>
          <p className="font-display italic text-2xl mt-3 text-[var(--color-text-muted)]">
            No CEO deliberations yet.
          </p>
          <p className="mt-2 font-mono text-[11px] tracking-wider uppercase text-[var(--color-text-dim)]">
            Escalations from L1–L3 agents will appear here once the CEO weighs in.
          </p>
        </div>
      )}

      {decisions.length > 0 && (
        <section className="space-y-3">
          {decisions.map((d, i) => (
            <DecisionCard key={d.id} decision={d} index={i} />
          ))}
        </section>
      )}
    </div>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────

function DecisionCard({ decision, index }: { decision: CeoDecision; index: number }) {
  const v = VERDICT_TOKENS[decision.decision.verdict];
  const sev = severityTokens(decision.escalation.severity);

  return (
    <div
      className="panel p-4 sm:p-5 reveal"
      style={{ animationDelay: `${Math.min(index * 40, 600)}ms` }}
    >
      {/* Top row: timestamp · from · severity · verdict */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="font-mono text-[11px] tabular-nums text-[var(--color-text-muted)]">
          {formatTimestamp(decision.createdAt)}
        </span>

        <span className="font-mono text-[10px] tracking-wider uppercase text-[var(--color-text-dim)]">
          from <span className="text-[var(--color-text)]">{decision.escalation.from || "—"}</span>
        </span>

        {decision.escalation.severity && (
          <span
            className="pill"
            style={{ color: sev.color, borderColor: sev.border, background: sev.bg }}
          >
            {decision.escalation.severity.toUpperCase()}
          </span>
        )}

        <span className="ml-auto" />

        <span
          className="pill"
          style={{ color: v.color, borderColor: v.border, background: v.bg }}
        >
          {v.label}
        </span>
      </div>

      {/* Reason */}
      {decision.decision.reason && (
        <p className="mt-3 text-sm text-[var(--color-text)] leading-relaxed">
          {decision.decision.reason}
        </p>
      )}

      {/* Escalation context — small, dimmer */}
      {decision.escalation.reason && (
        <p className="mt-2 text-xs text-[var(--color-text-muted)] leading-relaxed">
          <span className="font-mono text-[10px] tracking-wider uppercase text-[var(--color-text-dim)] mr-2">
            escalation
          </span>
          {decision.escalation.reason}
        </p>
      )}

      {/* Footer: conditions + alert flag */}
      {(decision.decision.conditions?.length || decision.decision.alertFounders) && (
        <div className="mt-4 pt-3 border-t border-[var(--color-border)]/60 flex flex-wrap items-start gap-x-4 gap-y-2">
          {decision.decision.conditions && decision.decision.conditions.length > 0 && (
            <ul className="space-y-1">
              {decision.decision.conditions.map((c, j) => (
                <li
                  key={j}
                  className="font-mono text-[11px] text-[var(--color-text-muted)] leading-snug"
                >
                  <span className="text-[var(--color-accent)] mr-2">▶</span>
                  {c}
                </li>
              ))}
            </ul>
          )}

          {decision.decision.alertFounders && (
            <span
              className="pill ml-auto"
              style={{
                color: "var(--color-status-error)",
                borderColor: "rgba(239, 68, 68, 0.6)",
                background: "rgba(239, 68, 68, 0.12)",
              }}
            >
              ALERT FOUNDERS
            </span>
          )}
        </div>
      )}
    </div>
  );
}
